import { EyeIcon, ListIcon } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  VisuallyHidden,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState, useSyncExternalStore } from "react";
import { Outlet } from "react-router";

import { StepFormProvider } from "@/client/providers/step-form";

import { BuilderNavigation } from "./_components/builder-navigation";
import { BuilderPreview } from "./_components/preview/builder-preview";
import { BuilderScroll } from "./_components/builder-scroll";
import { BuilderSidebar } from "./_components/sidebar/builder-sidebar";
import { ReuseResumeDialog } from "./_components/reuse-resume-dialog";
import { LeftSidebar } from "./sidebars/left";

const DESKTOP_PREVIEW_QUERY = "(min-width: 1280px)";

const subscribeToDesktopPreview = (callback: () => void) => {
  const query = window.matchMedia(DESKTOP_PREVIEW_QUERY);

  query.addEventListener("change", callback);

  return () => {
    query.removeEventListener("change", callback);
  };
};

const getDesktopPreviewSnapshot = () => window.matchMedia(DESKTOP_PREVIEW_QUERY).matches;

const getServerPreviewSnapshot = () => false;

const BuilderWorkspace = () => {
  const [collapsed, setCollapsed] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  /*
   * Empêche la création simultanée
   * d’un iframe desktop et mobile.
   */
  const desktopPreview = useSyncExternalStore(
    subscribeToDesktopPreview,
    getDesktopPreviewSnapshot,
    getServerPreviewSnapshot,
  );

  return (
    <div
      data-space="client"
      className="flex h-dvh min-h-0 w-full overflow-hidden bg-white font-sans text-slate-900"
    >
      <aside className="hidden shrink-0 lg:block">
        <BuilderSidebar
          collapsed={collapsed}
          onCollapse={() => {
            setCollapsed((current) => !current);
          }}
        />
      </aside>

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#f8f7ff]">
        <BuilderScroll className="min-h-0 flex-1 px-3 pb-4 pt-3 sm:px-6 sm:py-4 lg:px-8">
          <ReuseResumeDialog />
          <LeftSidebar />
        </BuilderScroll>

        <div className="relative z-20 shrink-0 border-t border-indigo-100 bg-white/95 backdrop-blur-md">
          <BuilderNavigation />
        </div>

        <nav
          aria-label="Outils du builder"
          className="relative z-30 grid shrink-0 grid-cols-2 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] xl:hidden"
        >
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen(true);
            }}
            className={cn(
              "flex min-h-12 items-center justify-center gap-2 px-3 text-xs font-bold text-[#071451]",
              "transition-colors hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-inset focus-visible:ring-indigo-500",
            )}
          >
            <ListIcon aria-hidden size={18} weight="bold" />
            Rubriques
          </button>

          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={previewOpen}
            onClick={() => {
              setPreviewOpen(true);
            }}
            className={cn(
              "flex min-h-12 items-center justify-center gap-2 border-l border-slate-200",
              "px-3 text-xs font-bold text-[#071451] transition-colors hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
              "focus-visible:ring-indigo-500",
            )}
          >
            <EyeIcon aria-hidden size={18} weight="bold" />
            Aperçu
          </button>
        </nav>
      </main>

      {desktopPreview && (
        <section aria-label="Aperçu du CV" className="min-h-0 w-[42%] min-w-[470px] shrink-0">
          <BuilderPreview>
            <Outlet />
          </BuilderPreview>
        </section>
      )}

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <VisuallyHidden>
          <SheetHeader>
            <SheetTitle>Rubriques</SheetTitle>

            <SheetDescription>
              Naviguez entre les différentes rubriques de votre CV.
            </SheetDescription>
          </SheetHeader>
        </VisuallyHidden>

        <SheetContent
          side="left"
          className="h-dvh w-[min(88vw,320px)] max-w-none border-0 p-0 [&>button]:hidden"
        >
          <BuilderSidebar
            collapsed={false}
            onCollapse={() => {
              setMenuOpen(false);
            }}
            onNavigate={() => {
              setMenuOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>

      {!desktopPreview && (
        <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle>Aperçu du CV</SheetTitle>

              <SheetDescription>Consultez le rendu actuel de votre CV.</SheetDescription>
            </SheetHeader>
          </VisuallyHidden>

          <SheetContent
            side="right"
            className="h-dvh w-full max-w-none border-0 p-0 sm:max-w-[620px] [&>button]:hidden"
          >
            <div className="flex h-full min-h-0 flex-col bg-white">
              <div className="flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
                <div className="flex items-center gap-2">
                  <EyeIcon aria-hidden size={18} weight="bold" className="text-indigo-700" />

                  <span className="text-xs font-bold text-[#071451]">Aperçu du CV</span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setPreviewOpen(false);
                  }}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-bold text-indigo-700",
                    "transition-colors hover:bg-indigo-50 focus-visible:outline-none",
                    "focus-visible:ring-2 focus-visible:ring-indigo-500",
                  )}
                >
                  Fermer
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-hidden">
                <BuilderPreview>
                  <Outlet />
                </BuilderPreview>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export const BuilderLayout = () => (
  <StepFormProvider>
    <BuilderWorkspace />
  </StepFormProvider>
);
